/**
 * L'onglet « Séances » de la fiche d'un élève, et la NOTE DE SÉANCE en fenêtre
 * — chantier « manuel vivant », Φ-MV-5 (MV-TUT-05, F-11).
 *
 * Une séance de tutorat s'ouvre (planifiée, ou consignée d'emblée comme faite),
 * se clôt avec sa note — « Ce qui a été travaillé », « Convenu pour la
 * prochaine fois » — et se relit ici, de la plus récente à la plus ancienne.
 *
 * ─── Qui lit la note ────────────────────────────────────────────────────────
 *
 * LE TUTEUR QUI L'A ÉCRITE, et lui seul (`DECISIONS-ARCHITECTE.md`, point 17 ;
 * l'agence n'est pas dans cette phase). Elle est chiffrée en base ; pour tout
 * autre lecteur admis de la séance, le serveur rend `noteLisible: false` et
 * deux champs vides — l'écran le DIT, il n'affiche pas « aucune note ». La
 * fenêtre le rappelle sous son titre : on écrit autrement quand on sait qui lit.
 *
 * ─── Ce que les trois tuiles comptent ───────────────────────────────────────
 *
 * Des séances — faites, annulées, faites sans note. Des dénombrements que le
 * serveur a faits (`SeancesTuteurResponse`) : l'écran ne recompte rien, et
 * « sans note » a UNE définition (note vide après retrait des espaces).
 */
import {
  MAX_CARACTERES_NOTE_SEANCE,
  type EtatSeanceTutorat,
  type SeanceTutoratDto,
} from '@silex/shared';
import { useId, useMemo, useState } from 'react';

import { api } from '../../api/client';
import { Badge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { Chargement, ErreurRessource } from '../../components/EtatRessource';
import { Fenetre } from '../../components/Fenetre';
import {
  brouillonDeSeance,
  brouillonDeSeanceNeuve,
  corpsDeSeance,
  dureesDeSeance,
  reglesDeSeance,
  type BrouillonSeance,
} from '../../lib/manuel-tuteur-format';
import type { BadgeTone } from '../../lib/suivi-format';
import { formatDateHeure } from '../../lib/tuteur-format';
import { messageErreur, useAsyncRechargeable } from '../../lib/useAsync';

const TON_ETAT: Readonly<Record<EtatSeanceTutorat, BadgeTone>> = {
  a_venir: 'en-cours',
  faite: 'acquis',
  annulee: 'a-revoir',
};

/** Les trois états, dits comme le formulaire les propose (MV-TUT-05). */
const CHOIX_ETAT: readonly { readonly etat: EtatSeanceTutorat; readonly libelle: string }[] = [
  { etat: 'a_venir', libelle: 'À venir' },
  { etat: 'faite', libelle: 'Séance faite' },
  { etat: 'annulee', libelle: 'Annulée' },
];

/** `null` : fermée ; `'neuve'` : ouvrir une séance ; une séance : la noter. */
type Saisie = null | 'neuve' | SeanceTutoratDto;

export function SeancesEleve({
  eleveId,
  prenom,
  aujourdhui,
  peutEcrire,
  surChangement,
}: {
  readonly eleveId: string;
  readonly prenom: string;
  /** Date civile du foyer, lue de la fiche : la référence du « dans sept jours ». */
  readonly aujourdhui: string;
  readonly peutEcrire: boolean;
  readonly surChangement: () => void;
}) {
  const { resource: liste, recharger } = useAsyncRechargeable(
    useMemo(() => () => api.manuelSeances(eleveId), [eleveId]),
  );
  const [saisie, setSaisie] = useState<Saisie>(null);

  if (liste.status === 'loading') return <Chargement quoi="ses séances" />;
  if (liste.status === 'error') return <ErreurRessource message={liste.message} />;
  const { seances } = liste.data;

  return (
    <div className="stack" data-testid="manuel-seances">
      <section className="card">
        <div className="row-between wrap">
          <div className="stat-tile-row stat-tile-row--serre">
            <div className="stat-tile">
              <span className="stat-tile__value" data-testid="manuel-seances-faites">
                {liste.data.faites}
              </span>
              <span className="stat-tile__label">Séances faites</span>
            </div>
            <div className="stat-tile">
              <span className="stat-tile__value">{liste.data.annulees}</span>
              <span className="stat-tile__label">Annulées</span>
            </div>
            <div className="stat-tile">
              <span className="stat-tile__value">{liste.data.sansNote}</span>
              <span className="stat-tile__label">Sans note</span>
            </div>
          </div>
          <div className="row wrap">
            {/* « Préparer la séance » est dans l'en-tête de la fiche, au-dessus de
                cet onglet : jamais deux fois la même action (MV-UX-02 ; revue UX
                `UX-7-12`). */}
            {peutEcrire ? (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setSaisie('neuve')}
                data-testid="manuel-seance-ouvrir"
              >
                Ouvrir une séance
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {seances.length === 0 ? (
        <EmptyState title={`Aucune séance pour ${prenom}.`}>
          Une séance ouverte ici garde sa note et ce qui est convenu pour la prochaine fois.
        </EmptyState>
      ) : (
        <div className="list" data-testid="manuel-seances-fil">
          {seances.map((s) => (
            <div key={s.id} className="li li--haut" data-testid="manuel-seance">
              <span className="li-main">
                <span className="li-title">{formatDateHeure(s.debut)}</span>
                <span className="li-sub">
                  {s.dureeMin} min{s.tuteur === null ? '' : ` · ${s.tuteur}`}
                </span>
                <NoteLue seance={s} />
              </span>
              <Badge tone={TON_ETAT[s.etat]} label={s.libelleEtat} />
              {peutEcrire && s.laMienne ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setSaisie(s)}
                  data-testid="manuel-seance-noter"
                >
                  {s.etat === 'a_venir' || s.sansNote ? 'Noter' : 'Modifier la note'}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {saisie !== null ? (
        <NoteSeance
          eleveId={eleveId}
          prenom={prenom}
          seance={saisie === 'neuve' ? null : saisie}
          aujourdhui={aujourdhui}
          surFermeture={() => setSaisie(null)}
          surEnregistre={() => {
            setSaisie(null);
            recharger();
            surChangement();
          }}
        />
      ) : null}
    </div>
  );
}

/** Ce que le fil dit de la note d'une séance — ou pourquoi il n'en dit rien. */
function NoteLue({ seance: s }: { readonly seance: SeanceTutoratDto }) {
  if (s.etat === 'a_venir') return null;
  if (!s.noteLisible) {
    return <span className="li-sub">Note réservée au tuteur qui l’a écrite.</span>;
  }
  if (s.notes === null && s.suites === null) {
    return <span className="li-sub">Aucune note pour cette séance.</span>;
  }
  return (
    <>
      {s.notes === null ? null : (
        <span className="seance-note" data-testid="manuel-seance-note">
          <span className="lbl">
            {s.etat === 'annulee' ? 'Motif de l’annulation' : 'Ce qui a été travaillé'}
          </span>
          {s.notes}
        </span>
      )}
      {s.suites === null ? null : (
        <span className="seance-note" data-testid="manuel-seance-suites">
          <span className="lbl">Convenu pour la prochaine fois</span>
          {s.suites}
        </span>
      )}
    </>
  );
}

/* ─────────────────────────── la note, en fenêtre ─────────────────────────── */

function NoteSeance({
  eleveId,
  prenom,
  seance,
  aujourdhui,
  surFermeture,
  surEnregistre,
}: {
  readonly eleveId: string;
  readonly prenom: string;
  /** `null` : une séance neuve. */
  readonly seance: SeanceTutoratDto | null;
  readonly aujourdhui: string;
  readonly surFermeture: () => void;
  readonly surEnregistre: () => void;
}) {
  const id = useId();
  const [b, setB] = useState<BrouillonSeance>(() =>
    seance === null ? brouillonDeSeanceNeuve(aujourdhui) : brouillonDeSeance(seance, new Date()),
  );
  const [manques, setManques] = useState<readonly string[]>([]);
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const changer = (morceau: Partial<BrouillonSeance>): void =>
    setB((avant) => ({ ...avant, ...morceau }));

  const enregistrer = async (): Promise<void> => {
    const regles = reglesDeSeance(b);
    setManques(regles);
    setRefus(null);
    if (regles.length > 0) return;
    setEnvoi(true);
    try {
      const corps = corpsDeSeance(b);
      if (seance === null) {
        await api.manuelOuvrirSeance(eleveId, {
          debut: corps.debut,
          dureeMin: corps.dureeMin,
          etat: corps.etat,
          ...(corps.notes === null ? {} : { notes: corps.notes }),
          ...(corps.suites === null ? {} : { suites: corps.suites }),
        });
      } else if (corps.etat === 'a_venir') {
        /* Replanifier ne touche pas à la note : ses champs sont masqués, ils
           ne partent pas (un champ absent est laissé tel quel). */
        await api.manuelNoterSeance(seance.id, {
          debut: corps.debut,
          dureeMin: corps.dureeMin,
          etat: corps.etat,
        });
      } else {
        await api.manuelNoterSeance(seance.id, corps);
      }
      surEnregistre();
    } catch (err) {
      setRefus(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  const aVenir = b.etat === 'a_venir';
  return (
    <Fenetre
      titre={seance === null ? `Ouvrir une séance avec ${prenom}` : 'Note de séance'}
      sous={`Lue par vous seul : ni ${prenom} ni sa famille ne voient cette note.`}
      testId="seance-fenetre"
      surFermeture={surFermeture}
      actions={
        <>
          <button type="button" className="btn btn--ghost" onClick={surFermeture}>
            Annuler
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={envoi}
            onClick={() => void enregistrer()}
            data-testid="seance-enregistrer"
          >
            {aVenir ? 'Enregistrer la séance' : 'Enregistrer la note'}
          </button>
        </>
      }
    >
      <div className="segmented" role="group" aria-label="État de la séance">
        {CHOIX_ETAT.map((c) => (
          <button
            key={c.etat}
            type="button"
            className={c.etat === b.etat ? 'segmented__btn is-active' : 'segmented__btn'}
            aria-pressed={c.etat === b.etat}
            onClick={() => changer({ etat: c.etat })}
            data-testid={`seance-etat-${c.etat}`}
          >
            {c.libelle}
          </button>
        ))}
      </div>

      <div className="form-grid form-grid--serre">
        <div className="field">
          <label htmlFor={`${id}-date`}>Date</label>
          <input
            id={`${id}-date`}
            type="date"
            value={b.date}
            onChange={(e) => changer({ date: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-heure`}>Heure</label>
          <input
            id={`${id}-heure`}
            type="time"
            value={b.heure}
            onChange={(e) => changer({ heure: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-duree`}>Durée</label>
          <select
            id={`${id}-duree`}
            value={b.dureeMin}
            onChange={(e) => changer({ dureeMin: Number(e.target.value) })}
          >
            {dureesDeSeance().map((d) => (
              <option key={d} value={d}>
                {d} min
              </option>
            ))}
          </select>
        </div>
      </div>

      {aVenir ? (
        <p className="form-note">La note s’écrit une fois la séance faite ou annulée.</p>
      ) : (
        <>
          <div className="field">
            <label htmlFor={`${id}-notes`}>
              {b.etat === 'annulee' ? 'Motif de l’annulation' : 'Ce qui a été travaillé'}
            </label>
            <textarea
              id={`${id}-notes`}
              rows={4}
              maxLength={MAX_CARACTERES_NOTE_SEANCE}
              placeholder={
                b.etat === 'annulee'
                  ? 'Qui a annulé, quand, reprise prévue'
                  : 'Notions reprises, ce qui a débloqué, ce qui résiste encore'
              }
              value={b.notes}
              onChange={(e) => changer({ notes: e.target.value })}
              data-testid="seance-notes"
            />
          </div>
          {b.etat === 'annulee' ? null : (
            <div className="field">
              <label htmlFor={`${id}-suites`}>Convenu pour la prochaine fois</label>
              <textarea
                id={`${id}-suites`}
                rows={3}
                maxLength={MAX_CARACTERES_NOTE_SEANCE}
                placeholder={`Ce que ${prenom} fait d’ici là`}
                value={b.suites}
                onChange={(e) => changer({ suites: e.target.value })}
                data-testid="seance-suites"
              />
            </div>
          )}
        </>
      )}

      {manques.length > 0 ? (
        <div className="callout callout--rare" role="alert">
          {manques.join(' ')}
        </div>
      ) : null}
      {refus !== null ? (
        <div className="callout callout--rare" role="alert" data-testid="seance-refus">
          <strong>Séance non enregistrée.</strong> {refus}
        </div>
      ) : null}
    </Fenetre>
  );
}
